import { useState } from "react";
import { Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import type { ActivationSignalProcessing } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { Field } from "./DetailSection";
import { TimelineValue } from "./TimelineValue";

/** a3s's handling of the signal, collapsed: it is for investigating, not for reading every signal. */
export function SignalProcessing({ processing }: { processing: ActivationSignalProcessing }) {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useUnistyles();

  return (
    <View style={styles.processing}>
      <Pressable
        onPress={() => setExpanded((prev) => !prev)}
        style={styles.processingToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
      >
        <Icon name={expanded ? "chevronUp" : "chevronDown"} size={16} color={theme.colors.textMuted} />
        <Text textStyle="sm/medium" color="textMuted">
          a3s processing
        </Text>
      </Pressable>
      {expanded && <SignalProcessingFields processing={processing} />}
    </View>
  );
}

/** What triage made of the signal, how it was delivered, and when each step happened. */
export function SignalProcessingFields({ processing }: { processing: ActivationSignalProcessing }) {
  const { triage, delivery, failures } = processing;
  const settledLabel = SETTLED_LABEL[processing.status];

  return (
    <View gap="md">
      <Field label="Triage">
        {triage.status === "classified" ?
          [triage.response, triage.outcome, triage.model && `(${triage.model})`].filter(Boolean).join(" · ")
        : TRIAGE_ABSENT[triage.status]}
      </Field>
      {triage.status === "classified" && (
        <>
          <Field label="Threat score">{formatScore(triage.threatScore)}</Field>
          <Field label="Detection confidence">{formatScore(triage.detectionConfidence)}</Field>
          <Field label="Summary">{formatText(triage.summary)}</Field>
        </>
      )}
      <Field label="Status">{STATUS_LABEL[processing.status]}</Field>
      <Field label="Provider">{processing.provider}</Field>
      {delivery !== undefined && (
        <>
          <Field label="Action">
            {delivery.existingTicketNo !== undefined ?
              `${delivery.action} · open activation ${delivery.existingTicketNo}`
            : delivery.action}
          </Field>
          {delivery.mapping !== undefined && (
            <Field label="Mapping">
              {delivery.mapping.classificationKey === delivery.mapping.deliveredKey ?
                `${delivery.mapping.deliveredKey} (no remap)`
              : `${delivery.mapping.classificationKey} → ${delivery.mapping.deliveredKey} (remapped)`}
            </Field>
          )}
          <Field label="Delivered as">
            {[
              delivery.typeNo !== undefined && `Type ${delivery.typeNo}`,
              delivery.mapping !== undefined && `Priority ${delivery.mapping.deliveredPriority}`,
            ]
              .filter(Boolean)
              .join(" · ") || EMPTY}
          </Field>
          <Field label="Policy">{delivery.policy.length > 0 ? delivery.policy.join("\n") : EMPTY}</Field>
        </>
      )}
      <Field label="Captured">{formatInstant(processing.capturedAt)}</Field>
      <Field label="Received by a3s">
        <TimelineValue from={processing.capturedAt} at={processing.receivedAt} sinceLabel="after captured" />
      </Field>
      {settledLabel !== undefined && (
        <Field label={settledLabel}>
          <TimelineValue from={processing.receivedAt} at={processing.settledAt} sinceLabel="after received" />
        </Field>
      )}
      <Field label={`Failed attempts (${String(failures.length)})`}>
        {failures.length === 0 ?
          EMPTY
        : failures
            .map((f) => `${formatInstant(f.at)} · ${f.transient ? "transient" : "permanent"} · ${f.reason}`)
            .join("\n")
        }
      </Field>
    </View>
  );
}

const TRIAGE_ABSENT = {
  test_signal: "Skipped: test signal",
  none: "None recorded: triage failed or timed out, or this provider does not triage",
} as const;

const STATUS_LABEL = {
  pending: "Pending delivery",
  delivered: "Delivered",
  poisoned: "Poisoned: delivery gave up",
  expired: "Expired before delivery",
} as const;

const SETTLED_LABEL = {
  pending: undefined,
  delivered: "Delivered",
  poisoned: "Poisoned",
  expired: "Expired",
} as const;

/** A 0–1 score to two places — the scale the classifier reports on. */
function formatScore(score: number | undefined): string {
  return score === undefined ? EMPTY : score.toFixed(2);
}

const styles = StyleSheet.create((theme) => ({
  processing: { gap: theme.spacing.md, paddingTop: theme.spacing.xs },
  processingToggle: { flexDirection: "row", alignItems: "center", gap: theme.spacing.xs, alignSelf: "flex-start" },
}));
